// src/version/index.js — the "new version available" cluster, SHARED by
// index.html and admin.html (bundled to dist/version.js; both apps previously
// carried hand-synced divergent copies — this is the single copy now).
//
// Moved VERBATIM from index.html (the canonical, fully-commented variant;
// admin.html's copy was a stylistic rewrite of the same logic).
//
// Page-global contract (deliberate — state stays in the HTML):
//   VERSION_CHECK_INTERVAL_MS, _versionBannerDismissed  — declared inline in
//     BOTH HTML files; read/written here and by Notices' showUpdateBanner.
//   showUpdateBanner / hideUpdateBanner — app-provided: index binds them from
//     the Notices bundle, admin keeps inline copies. Resolved by free-name
//     lookup at call time (first call is 30s after DOMContentLoaded, long
//     after both apps have bound them).
//   APP_VERSION — from /version.js (self.APP_VERSION).

async function checkForUpdate() {
  // Only run when online — offline checks are pointless and would noisily fail
  if (typeof DB !== 'undefined' && typeof DB.isOnline === 'function' && !DB.isOnline()) {
    return;
  }
  try {
    // Use a direct fetch (not DB.api) because (a) this is a public endpoint
    // and (b) we want the request to NOT go through the 30s timeout — a
    // version check timing out shouldn't show an error toast to the user.
    var ctrl = new AbortController();
    var t = setTimeout(function(){ ctrl.abort(); }, 8000);
    var workerUrl = (typeof DB !== 'undefined' && DB.workerUrl) ? DB.workerUrl() : '';
    if (!workerUrl) { clearTimeout(t); return; }
    var res = await fetch(workerUrl + '/api/version', { cache: 'no-store', signal: ctrl.signal });
    clearTimeout(t);
    if (!res.ok) return;
    var data = await res.json();
    if (!data || !data.version) return;

    if (data.version !== APP_VERSION) {
      showUpdateBanner();
      // v123.18 — proactively pull a fresh service worker so the cache self-heals
      // even when staff only ever DISMISS the banner. A successful update installs
      // the new SW, whose activate() deletes the stale caches + claims clients, so
      // the NEXT navigation is served fresh. Best-effort; failures are silent.
      try {
        if ('serviceWorker' in navigator && navigator.serviceWorker.getRegistrations) {
          navigator.serviceWorker.getRegistrations().then(function (rs) {
            rs.forEach(function (r) { try { r.update(); } catch (e) {} });
          }).catch(function () {});
        }
      } catch (e) {}
    } else {
      hideUpdateBanner();
    }
  } catch (e) {
    // Silent — version check failures shouldn't disrupt the app
  }
}

// v105.x: showUpdateBanner → Notices.notify.showUpdateBanner

// v105.x: hideUpdateBanner → Notices.notify.hideUpdateBanner

async function applyUpdate() {
  // v123.18 — the service worker serves the app's HTML + JS stale-while-
  // revalidate, so a PLAIN reload keeps serving the OLD cached bundle. Devices
  // were getting stuck many versions behind, the banner reappearing after every
  // reload (the old code here only skipWaiting'd + reloaded — it never cleared
  // the cache, despite the comment claiming "unregister and reload"). The only
  // reliable escape is to tear the service worker + caches DOWN, then reload —
  // the reload is then served fresh from the network and a clean worker
  // re-registers. This mirrors window.__elcBoot.hardReload (the boot-recovery
  // path) which is proven to unstick a wedged cache.
  var reload = function () {
    try { location.reload(true); } catch (e) { try { location.reload(); } catch (_) {} }
  };
  try {
    var jobs = [];
    if ('serviceWorker' in navigator && navigator.serviceWorker.getRegistrations) {
      jobs.push(navigator.serviceWorker.getRegistrations()
        .then(function (rs) {
          return Promise.all(rs.map(function (r) {
            try { if (r.waiting) r.waiting.postMessage({ type: 'SKIP_WAITING' }); } catch (e) {}
            return r.unregister();
          }));
        })
        .catch(function () {}));
    }
    if (typeof caches !== 'undefined' && caches.keys) {
      jobs.push(caches.keys()
        .then(function (ks) { return Promise.all(ks.map(function (k) { return caches.delete(k); })); })
        .catch(function () {}));
    }
    // Never let a hung teardown trap the user on the old version — reload after
    // 1.5s no matter what.
    Promise.race([Promise.all(jobs), new Promise(function (r) { setTimeout(r, 1500); })]).then(reload, reload);
  } catch (e) { reload(); }
}

// Wire up banner buttons + start polling once DOM is ready
function initVersionCheck() {
  var updBtn = document.getElementById('updateBannerUpdateBtn');
  var dismissBtn = document.getElementById('updateBannerDismissBtn');
  if (updBtn) updBtn.addEventListener('click', applyUpdate);
  if (dismissBtn) dismissBtn.addEventListener('click', function(){
    _versionBannerDismissed = true;
    hideUpdateBanner();
    // Reset the dismissal after one poll interval — so if a NEWER version
    // appears later in the same session, the banner shows again.
    setTimeout(function(){ _versionBannerDismissed = false; }, VERSION_CHECK_INTERVAL_MS);
  });
  // Initial check after a 30-second delay so it doesn't compete with app boot
  setTimeout(checkForUpdate, 30 * 1000);
  // Subsequent checks every 30 minutes
  setInterval(checkForUpdate, VERSION_CHECK_INTERVAL_MS);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initVersionCheck);
} else {
  initVersionCheck();
}

// Explicit globals: these were top-level function declarations in the HTML
// (window properties); keep them reachable (console ops, future callers).
window.checkForUpdate = checkForUpdate;
window.applyUpdate = applyUpdate;
window.initVersionCheck = initVersionCheck;
